import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Flame, Trophy, Calendar, Shield, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { getCourseList, CourseMetadata } from '../config/courses';
import { useAuth } from '../hooks/useAuth';
import {
  getQuizResults,
  getStreak,
  getUserActivityLog,
  getUserDailyStreaks,
  getAllUserQuizResults,
  getAllUserFlashcardProgress,
} from '../lib/supabase';
import { QuizResult, ActivityLog, DailyStreak, FlashcardProgress } from '../types';
import { ResetModal } from './ResetModal';
import { LoadingSpinner } from './LoadingSpinner';

// ============================================
// MEDAL DEFINITIONS
// ============================================

interface Medal {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'streak' | 'quiz' | 'vocab' | 'course' | 'time';
  requirement: number;
  unit: string;
}

const MEDALS: Medal[] = [
  // Streak Medals
  { id: 'streak-3', name: 'Erste Schritte', description: '3 Tage Streak', icon: '🥉', category: 'streak', requirement: 3, unit: 'Tage' },
  { id: 'streak-7', name: 'Fleissig', description: '7 Tage Streak', icon: '🥈', category: 'streak', requirement: 7, unit: 'Tage' },
  { id: 'streak-14', name: 'Durchhalter', description: '14 Tage Streak', icon: '🥇', category: 'streak', requirement: 14, unit: 'Tage' },
  { id: 'streak-30', name: 'Unstoppable', description: '30 Tage Streak', icon: '💎', category: 'streak', requirement: 30, unit: 'Tage' },
  // Quiz Medals
  { id: 'quiz-first', name: 'Quiz-Starter', description: 'Erstes Quiz', icon: '⭐', category: 'quiz', requirement: 1, unit: 'Quiz' },
  { id: 'quiz-10', name: 'Quiz-Profi', description: '10 Quizze', icon: '⭐⭐', category: 'quiz', requirement: 10, unit: 'Quizze' },
  { id: 'quiz-perfect', name: 'Perfektionist', description: 'Ein Quiz mit 100%', icon: '🎯', category: 'quiz', requirement: 100, unit: '%' },
  { id: 'quiz-rising', name: 'Aufsteiger', description: '3x steigender Score', icon: '📈', category: 'quiz', requirement: 3, unit: 'Reihe' },
  // Vocab Medals
  { id: 'vocab-50', name: 'Vokabel-Sammler', description: '50 Vokabeln gelernt', icon: '📚', category: 'vocab', requirement: 50, unit: 'Vokabeln' },
  { id: 'vocab-king', name: 'Vokabel-Koenig', description: 'Alle Vokabeln eines Kurses', icon: '👑', category: 'vocab', requirement: 100, unit: '%' },
  { id: 'vocab-weakness', name: 'Schwaechen-Besieger', description: 'Schwaechen-Modus 0 Fehler', icon: '🔄', category: 'vocab', requirement: 1, unit: 'Mal' },
  // Course Medals
  { id: 'course-complete', name: 'Kurs geschafft', description: 'Alle Module >= 90%', icon: '🏆', category: 'course', requirement: 1, unit: 'Kurs' },
  { id: 'course-multi', name: 'Multi-Talent', description: '2+ Kurse geschafft', icon: '🌍', category: 'course', requirement: 2, unit: 'Kurse' },
  // Time Medals
  { id: 'time-1h', name: '1 Stunde', description: '1 Stunde Lernzeit', icon: '⏱️', category: 'time', requirement: 60, unit: 'Min.' },
  { id: 'time-5h', name: '5 Stunden', description: '5 Stunden Lernzeit', icon: '⏱️⏱️', category: 'time', requirement: 300, unit: 'Min.' },
  { id: 'time-50sess', name: 'Lernmaschine', description: '50 Sessions', icon: '🔥', category: 'time', requirement: 50, unit: 'Sessions' },
];

// ============================================
// HELPER FUNCTIONS
// ============================================

const MONTH_NAMES = ['Januar', 'Februar', 'Maerz', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

function getActivityIcon(action: string): string {
  switch (action) {
    case 'quiz': return '📝';
    case 'vocab_quiz': return '📝';
    case 'vocab_match': return '🎯';
    case 'fill_in': return '✏️';
    case 'transform_quiz': return '🔄';
    case 'flashcards': return '🃏';
    case 'text_lesson': return '📖';
    case 'lesson': return '🎧';
    case 'written_quiz': return '✍️';
    case 'math_exercise': return '🧮';
    case 'song_listen': return '🎵';
    default: return '📊';
  }
}

function getActivityLabel(action: string): string {
  switch (action) {
    case 'quiz': return 'Quiz';
    case 'vocab_quiz': return 'Vokabel-Quiz';
    case 'vocab_match': return 'Vokabel-Match';
    case 'fill_in': return 'Lückentext';
    case 'transform_quiz': return 'Satzumwandlung';
    case 'flashcards': return 'Lernkarten';
    case 'text_lesson': return 'Lernstoff';
    case 'lesson': return 'Lektion';
    case 'written_quiz': return 'Schriftlich';
    case 'math_exercise': return 'Rechenaufgaben';
    case 'song_listen': return 'Song';
    default: return action;
  }
}

function formatActivityDetails(action: string, details: Record<string, any>): string {
  switch (action) {
    case 'quiz':
      if (details.percentage !== undefined) {
        return `${details.percentage}% (${details.score}/${details.total})`;
      }
      return '';
    case 'vocab_match':
      return `${details.pairs_matched || 0} Paare, ${(details.attempts || 0) - (details.pairs_matched || 0)} Fehler`;
    case 'fill_in':
      if (details.percentage !== undefined) {
        return `${details.percentage}% (${details.score}/${details.total})`;
      }
      return '';
    case 'transform_quiz':
      if (details.percentage !== undefined) {
        return `${details.percentage}% (${details.score}/${details.total})`;
      }
      return '';
    case 'flashcards':
      return `${details.cards_reviewed || details.total || 0} Karten (${details.cards_known || details.knew || 0} gewusst)`;
    case 'text_lesson':
      return details.lesson_title || '';
    case 'lesson':
      return details.lesson_id || '';
    case 'math_exercise':
      return `${details.score || 0}/${details.total || 0} Punkte (${details.exercises_completed || 0} Aufgaben)`;
    case 'vocab_quiz':
      if (details.percentage !== undefined) {
        return `${details.percentage}% (${details.score}/${details.total})`;
      }
      return '';
    case 'song_listen':
      return `${details.song_title || '?'} – ${details.listened_percentage || 0}% gehoert`;
    default:
      return '';
  }
}

function getCourseLabel(courseId: string): string {
  switch (courseId) {
    case 'eu-geographie': return 'EU Geographie';
    case 'english-test3': return 'English Test 3';
    default: return courseId;
  }
}

function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' });
}

// ============================================
// MEDAL CALCULATION
// ============================================

interface MedalStatus {
  medal: Medal;
  achieved: boolean;
  achievedAt?: string;
  progress: number;
  progressMax: number;
}

function calculateMedals(
  streak: number,
  quizResults: QuizResult[],
  activityLog: ActivityLog[],
  dailyStreaks: DailyStreak[],
  flashcardProgress: FlashcardProgress[],
  courseFilter?: string
): MedalStatus[] {
  // Filter data by course if specified
  const filteredQuizResults = courseFilter
    ? quizResults.filter(q => q.course_id === courseFilter)
    : quizResults;
  const filteredActivityLog = courseFilter
    ? activityLog.filter(a => a.details?.course_id === courseFilter)
    : activityLog;
  const filteredFlashcardProgress = courseFilter
    ? flashcardProgress.filter(fp => fp.course_id === courseFilter)
    : flashcardProgress;
  const medals: MedalStatus[] = [];

  // Calculate total learning time (minutes) - streaks are global, not per-course
  const totalMinutes = dailyStreaks.reduce((sum, ds) => sum + (ds.total_minutes || 0), 0);
  const totalSessions = filteredActivityLog.length;

  // Count vocab with knew=true
  const vocabKnown = filteredFlashcardProgress.filter(fp => fp.knew && fp.flashcard_id.startsWith('vocab_')).length;

  // Check for weakness mode completion with 0 errors
  const weaknessWins = filteredActivityLog.filter(a =>
    a.action === 'vocab_match' &&
    a.details?.is_weakness_mode &&
    (a.details?.attempts === a.details?.pairs_matched)
  ).length;

  // Check for rising scores (3 consecutive quizzes with increasing percentage)
  let risingCount = 0;
  let maxRising = 0;
  const sortedQuizzes = [...filteredQuizResults].sort((a, b) =>
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
  for (let i = 1; i < sortedQuizzes.length; i++) {
    if (sortedQuizzes[i].percentage > sortedQuizzes[i - 1].percentage) {
      risingCount++;
      maxRising = Math.max(maxRising, risingCount);
    } else {
      risingCount = 0;
    }
  }

  // Calculate each medal
  for (const medal of MEDALS) {
    let achieved = false;
    let progress = 0;
    let progressMax = medal.requirement;
    let achievedAt: string | undefined;

    switch (medal.id) {
      // Streak medals
      case 'streak-3':
      case 'streak-7':
      case 'streak-14':
      case 'streak-30':
        progress = streak;
        achieved = streak >= medal.requirement;
        break;

      // Quiz medals
      case 'quiz-first':
        progress = filteredQuizResults.length > 0 ? 1 : 0;
        achieved = filteredQuizResults.length >= 1;
        if (achieved && filteredQuizResults.length > 0) {
          achievedAt = filteredQuizResults[filteredQuizResults.length - 1].created_at;
        }
        break;
      case 'quiz-10':
        progress = Math.min(filteredQuizResults.length, 10);
        achieved = filteredQuizResults.length >= 10;
        break;
      case 'quiz-perfect':
        const perfectQuiz = filteredQuizResults.find(q => q.percentage === 100);
        progress = perfectQuiz ? 100 : Math.max(...filteredQuizResults.map(q => q.percentage), 0);
        achieved = !!perfectQuiz;
        if (perfectQuiz) achievedAt = perfectQuiz.created_at;
        break;
      case 'quiz-rising':
        progress = Math.min(maxRising, 3);
        progressMax = 3;
        achieved = maxRising >= 3;
        break;

      // Vocab medals
      case 'vocab-50':
        progress = Math.min(vocabKnown, 50);
        achieved = vocabKnown >= 50;
        break;
      case 'vocab-king':
        // Check if any course has all vocab known
        const courseVocabs = new Map<string, { total: number; known: number }>();
        filteredFlashcardProgress.forEach(fp => {
          if (fp.flashcard_id.startsWith('vocab_')) {
            const current = courseVocabs.get(fp.course_id) || { total: 0, known: 0 };
            current.total++;
            if (fp.knew) current.known++;
            courseVocabs.set(fp.course_id, current);
          }
        });
        let bestPercent = 0;
        courseVocabs.forEach(cv => {
          if (cv.total > 0) {
            const pct = (cv.known / cv.total) * 100;
            if (pct > bestPercent) bestPercent = pct;
            if (pct === 100) achieved = true;
          }
        });
        progress = Math.round(bestPercent);
        progressMax = 100;
        break;
      case 'vocab-weakness':
        progress = Math.min(weaknessWins, 1);
        progressMax = 1;
        achieved = weaknessWins >= 1;
        break;

      // Course medals (simplified - based on quiz results >= 90%)
      case 'course-complete':
        const courseResults = new Map<string, number>();
        filteredQuizResults.forEach(q => {
          const current = courseResults.get(q.course_id) || 0;
          if (q.percentage > current) courseResults.set(q.course_id, q.percentage);
        });
        const completedCourses = Array.from(courseResults.values()).filter(p => p >= 90).length;
        progress = completedCourses >= 1 ? 1 : 0;
        achieved = completedCourses >= 1;
        break;
      case 'course-multi':
        const courseResults2 = new Map<string, number>();
        filteredQuizResults.forEach(q => {
          const current = courseResults2.get(q.course_id) || 0;
          if (q.percentage > current) courseResults2.set(q.course_id, q.percentage);
        });
        const completedCourses2 = Array.from(courseResults2.values()).filter(p => p >= 90).length;
        progress = Math.min(completedCourses2, 2);
        achieved = completedCourses2 >= 2;
        break;

      // Time medals
      case 'time-1h':
        progress = Math.min(totalMinutes, 60);
        achieved = totalMinutes >= 60;
        break;
      case 'time-5h':
        progress = Math.min(totalMinutes, 300);
        achieved = totalMinutes >= 300;
        break;
      case 'time-50sess':
        progress = Math.min(totalSessions, 50);
        achieved = totalSessions >= 50;
        break;
    }

    medals.push({
      medal,
      achieved,
      achievedAt,
      progress,
      progressMax,
    });
  }

  return medals;
}

// ============================================
// TREND CHART COMPONENT
// ============================================

function TrendChart({ quizResults }: { quizResults: QuizResult[] }) {
  // Get last 10 results, reversed for chronological order
  const data = useMemo(() => {
    const results = [...quizResults].slice(0, 10).reverse();
    return results.map(r => ({
      percentage: r.percentage,
      date: formatDate(r.created_at),
      courseId: r.course_id,
    }));
  }, [quizResults]);

  if (data.length < 2) {
    return (
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        borderRadius: '12px',
        padding: '20px',
        textAlign: 'center',
        color: 'rgba(255,255,255,0.5)',
        fontSize: '14px',
      }}>
        Mind. 2 Quiz-Ergebnisse fuer Trend-Anzeige noetig
      </div>
    );
  }

  const width = 300;
  const height = 120;
  const padding = { top: 10, right: 10, bottom: 25, left: 30 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const xStep = chartWidth / (data.length - 1);

  // Build path
  const points = data.map((d, i) => ({
    x: padding.left + i * xStep,
    y: padding.top + chartHeight - (d.percentage / 100) * chartHeight,
    ...d,
  }));

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  // Calculate trend
  const firstHalf = data.slice(0, Math.ceil(data.length / 2));
  const secondHalf = data.slice(Math.ceil(data.length / 2));
  const avgFirst = firstHalf.reduce((s, d) => s + d.percentage, 0) / firstHalf.length;
  const avgSecond = secondHalf.reduce((s, d) => s + d.percentage, 0) / secondHalf.length;
  const trend = avgSecond - avgFirst;
  const trendColor = trend > 0 ? '#4caf50' : trend < 0 ? '#ef5350' : '#9fa8da';
  const trendIcon = trend > 0 ? '↗️' : trend < 0 ? '↘️' : '→';

  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      borderRadius: '12px',
      padding: '16px',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
      }}>
        <span style={{ color: '#e8eaf6', fontSize: '14px', fontWeight: 600 }}>
          Letzte {data.length} Quizze
        </span>
        <span style={{ color: trendColor, fontSize: '14px', fontWeight: 700 }}>
          {trendIcon} {trend > 0 ? '+' : ''}{Math.round(trend)}%
        </span>
      </div>

      <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map(pct => (
          <g key={pct}>
            <line
              x1={padding.left}
              y1={padding.top + chartHeight - (pct / 100) * chartHeight}
              x2={width - padding.right}
              y2={padding.top + chartHeight - (pct / 100) * chartHeight}
              stroke="rgba(255,255,255,0.1)"
              strokeDasharray="2,2"
            />
            <text
              x={padding.left - 5}
              y={padding.top + chartHeight - (pct / 100) * chartHeight + 4}
              fill="rgba(255,255,255,0.4)"
              fontSize="9"
              textAnchor="end"
            >
              {pct}
            </text>
          </g>
        ))}

        {/* Line path */}
        <path
          d={pathD}
          fill="none"
          stroke="#ffd54f"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points */}
        {points.map((p, i) => (
          <g key={i}>
            <circle
              cx={p.x}
              cy={p.y}
              r="4"
              fill={p.courseId === 'eu-geographie' ? '#42a5f5' : '#4caf50'}
              stroke="#0a0e27"
              strokeWidth="1"
            />
            {/* Date label for first and last */}
            {(i === 0 || i === points.length - 1) && (
              <text
                x={p.x}
                y={height - 5}
                fill="rgba(255,255,255,0.5)"
                fontSize="8"
                textAnchor="middle"
              >
                {p.date}
              </text>
            )}
          </g>
        ))}
      </svg>

      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '8px',
        fontSize: '11px',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#42a5f5' }} />
          <span style={{ color: 'rgba(255,255,255,0.5)' }}>EU Geo</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#4caf50' }} />
          <span style={{ color: 'rgba(255,255,255,0.5)' }}>English</span>
        </span>
      </div>
    </div>
  );
}

// ============================================
// MEDAL CARD COMPONENT
// ============================================

function MedalCard({ status }: { status: MedalStatus }) {
  const { medal, achieved, progress, progressMax } = status;
  const progressPct = Math.min((progress / progressMax) * 100, 100);

  return (
    <div style={{
      background: achieved
        ? 'linear-gradient(135deg, rgba(255, 213, 79, 0.15), rgba(255, 152, 0, 0.1))'
        : 'rgba(26, 26, 46, 0.8)',
      border: achieved
        ? '1px solid rgba(255, 213, 79, 0.3)'
        : '1px solid rgba(255,255,255,0.1)',
      borderRadius: '12px',
      padding: '12px',
      textAlign: 'center',
      opacity: achieved ? 1 : 0.6,
    }}>
      <div style={{ fontSize: '28px', marginBottom: '6px' }}>
        {achieved ? medal.icon : '🔒'}
      </div>
      <div style={{
        color: achieved ? '#ffd54f' : 'rgba(255,255,255,0.5)',
        fontSize: '12px',
        fontWeight: 700,
        marginBottom: '4px',
      }}>
        {medal.name}
      </div>
      <div style={{
        color: 'rgba(255,255,255,0.4)',
        fontSize: '10px',
        marginBottom: '6px',
      }}>
        {medal.description}
      </div>

      {!achieved && (
        <div style={{
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '4px',
          height: '4px',
          overflow: 'hidden',
        }}>
          <div style={{
            background: '#ffd54f',
            height: '100%',
            width: `${progressPct}%`,
            transition: 'width 0.3s',
          }} />
        </div>
      )}
      {!achieved && (
        <div style={{
          color: 'rgba(255,255,255,0.4)',
          fontSize: '9px',
          marginTop: '4px',
        }}>
          {progress}/{progressMax} {medal.unit}
        </div>
      )}
    </div>
  );
}

// ============================================
// MAIN PROFILE COMPONENT
// ============================================

export function Profile() {
  const { user, logout } = useAuth();
  const [streak, setStreak] = useState(0);
  const [recentResults, setRecentResults] = useState<QuizResult[]>([]);
  const [allQuizResults, setAllQuizResults] = useState<QuizResult[]>([]);
  const [activityLog, setActivityLog] = useState<ActivityLog[]>([]);
  const [dailyStreaks, setDailyStreaks] = useState<DailyStreak[]>([]);
  const [flashcardProgress, setFlashcardProgress] = useState<FlashcardProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showResetModal, setShowResetModal] = useState(false);

  // Month navigation
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  useEffect(() => {
    async function loadData() {
      if (!user?.id) return;

      try {
        const [streakData, resultsData, allResults, activities, streaks, flashcards] = await Promise.all([
          getStreak(user.id),
          getQuizResults(user.id, undefined, 5),
          getAllUserQuizResults(user.id),
          getUserActivityLog(user.id),
          getUserDailyStreaks(user.id),
          getAllUserFlashcardProgress(user.id),
        ]);
        setStreak(streakData);
        setRecentResults(resultsData);
        setAllQuizResults(allResults);
        setActivityLog(activities);
        setDailyStreaks(streaks);
        setFlashcardProgress(flashcards);
      } catch (error) {
        console.error('Error loading profile data:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, [user?.id]);

  // Calculate stats
  const totalLearningHours = useMemo(() => {
    const totalMinutes = dailyStreaks.reduce((sum, ds) => sum + (ds.total_minutes || 0), 0);
    return Math.round(totalMinutes / 60 * 10) / 10; // 1 decimal
  }, [dailyStreaks]);

  const totalSessions = activityLog.length;

  const avgScore = useMemo(() => {
    if (allQuizResults.length === 0) return 0;
    return Math.round(allQuizResults.reduce((sum, q) => sum + q.percentage, 0) / allQuizResults.length);
  }, [allQuizResults]);

  // Filter activities for selected month
  const monthActivities = useMemo(() => {
    return activityLog.filter(a => {
      const date = new Date(a.created_at);
      return date.getFullYear() === selectedMonth.year && date.getMonth() === selectedMonth.month;
    });
  }, [activityLog, selectedMonth]);

  // Month stats
  const monthStats = useMemo(() => {
    const monthStreaks = dailyStreaks.filter(ds => {
      const date = new Date(ds.date);
      return date.getFullYear() === selectedMonth.year && date.getMonth() === selectedMonth.month;
    });
    const minutes = monthStreaks.reduce((sum, ds) => sum + (ds.total_minutes || 0), 0);
    const sessions = monthActivities.length;

    const monthQuizResults = allQuizResults.filter(q => {
      const date = new Date(q.created_at);
      return date.getFullYear() === selectedMonth.year && date.getMonth() === selectedMonth.month;
    });
    const avg = monthQuizResults.length > 0
      ? Math.round(monthQuizResults.reduce((sum, q) => sum + q.percentage, 0) / monthQuizResults.length)
      : 0;

    return {
      hours: Math.round(minutes / 60 * 10) / 10,
      sessions,
      avgScore: avg,
    };
  }, [dailyStreaks, monthActivities, allQuizResults, selectedMonth]);

  // Medal course filter
  const [medalCourseFilter, setMedalCourseFilter] = useState<string | undefined>(undefined);
  const visibleCourses = useMemo(() => getCourseList(false), []);

  // Calculate medals (filtered by course)
  const medalStatuses = useMemo(() => {
    return calculateMedals(streak, allQuizResults, activityLog, dailyStreaks, flashcardProgress, medalCourseFilter);
  }, [streak, allQuizResults, activityLog, dailyStreaks, flashcardProgress, medalCourseFilter]);

  const achievedMedals = medalStatuses.filter(m => m.achieved).length;

  if (!user) {
    return <div className="loading">Laden...</div>;
  }

  if (isLoading) {
    return <LoadingSpinner text="Profil wird geladen..." />;
  }

  const getRoleLabel = (role: string) => {
    if (role === 'admin') return 'Administrator';
    if (role === 'parent') return 'Elternteil';
    return 'Schueler/in';
  };

  const navigateMonth = (direction: -1 | 1) => {
    setSelectedMonth(prev => {
      let newMonth = prev.month + direction;
      let newYear = prev.year;
      if (newMonth < 0) {
        newMonth = 11;
        newYear--;
      } else if (newMonth > 11) {
        newMonth = 0;
        newYear++;
      }
      return { year: newYear, month: newMonth };
    });
  };

  const canGoForward = () => {
    const now = new Date();
    return selectedMonth.year < now.getFullYear() ||
      (selectedMonth.year === now.getFullYear() && selectedMonth.month < now.getMonth());
  };

  return (
    <div className="profile-view" style={{ paddingBottom: '120px' }}>
      <h1>Mein Profil</h1>

      {/* Profile Card */}
      <div className="profile-card">
        <div className="profile-avatar">
          {user.name?.charAt(0).toUpperCase() || '?'}
        </div>
        <div className="profile-info">
          <h2>{user.name}</h2>
          <p className="profile-email">{user.email}</p>
          <span className="profile-role">{getRoleLabel(user.role)}</span>
        </div>
      </div>

      {/* Basic Stats */}
      <div className="profile-stats">
        <div className="stat-card">
          <Flame size={24} className="stat-icon fire" />
          <div className="stat-content">
            <span className="stat-value">{streak}</span>
            <span className="stat-label">Tage Streak</span>
          </div>
        </div>
        <div className="stat-card">
          <Trophy size={24} className="stat-icon trophy" />
          <div className="stat-content">
            <span className="stat-value">{achievedMedals}</span>
            <span className="stat-label">Medaillen</span>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* ACTIVITY OVERVIEW */}
      {/* ============================================ */}
      <div style={{ marginTop: '24px' }}>
        <h3 style={{
          color: '#ffd54f',
          fontSize: '18px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          📊 Meine Aktivitaet
        </h3>

        {/* 3 Key Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '10px',
          marginBottom: '16px',
        }}>
          <div style={{
            background: 'rgba(66, 165, 245, 0.15)',
            borderRadius: '12px',
            padding: '14px 8px',
            textAlign: 'center',
          }}>
            <div style={{ color: '#42a5f5', fontSize: '22px', fontWeight: 700 }}>
              {totalLearningHours}h
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>Lernzeit</div>
          </div>
          <div style={{
            background: 'rgba(76, 175, 80, 0.15)',
            borderRadius: '12px',
            padding: '14px 8px',
            textAlign: 'center',
          }}>
            <div style={{ color: '#4caf50', fontSize: '22px', fontWeight: 700 }}>
              {totalSessions}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>Sessions</div>
          </div>
          <div style={{
            background: 'rgba(255, 213, 79, 0.15)',
            borderRadius: '12px',
            padding: '14px 8px',
            textAlign: 'center',
          }}>
            <div style={{ color: '#ffd54f', fontSize: '22px', fontWeight: 700 }}>
              {avgScore}%
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>Ø Score</div>
          </div>
        </div>

        {/* Month Navigation */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '10px',
          padding: '10px 12px',
          marginBottom: '12px',
        }}>
          <button
            onClick={() => navigateMonth(-1)}
            style={{
              background: 'none',
              border: 'none',
              color: '#9fa8da',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <ChevronLeft size={20} />
          </button>

          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#e8eaf6', fontSize: '15px', fontWeight: 700 }}>
              {MONTH_NAMES[selectedMonth.month]} {selectedMonth.year}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
              {monthStats.hours}h • {monthStats.sessions} Sessions • {monthStats.avgScore}% Ø
            </div>
          </div>

          <button
            onClick={() => navigateMonth(1)}
            disabled={!canGoForward()}
            style={{
              background: 'none',
              border: 'none',
              color: canGoForward() ? '#9fa8da' : 'rgba(255,255,255,0.2)',
              cursor: canGoForward() ? 'pointer' : 'default',
              padding: '4px',
            }}
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Activity List */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '12px',
          maxHeight: '300px',
          overflowY: 'auto',
        }}>
          {monthActivities.length === 0 ? (
            <div style={{
              padding: '30px',
              textAlign: 'center',
              color: 'rgba(255,255,255,0.4)',
              fontSize: '14px',
            }}>
              Keine Aktivitaeten in diesem Monat
            </div>
          ) : (
            monthActivities.map((activity, index) => (
              <div
                key={activity.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 14px',
                  borderBottom: index < monthActivities.length - 1
                    ? '1px solid rgba(255,255,255,0.05)'
                    : 'none',
                }}
              >
                <div style={{ fontSize: '24px' }}>
                  {getActivityIcon(activity.action)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '2px',
                  }}>
                    <span style={{ color: '#e8eaf6', fontSize: '14px', fontWeight: 600 }}>
                      {getActivityLabel(activity.action)}
                    </span>
                    <span style={{
                      color: activity.details?.course_id === 'eu-geographie' ? '#42a5f5' : '#4caf50',
                      fontSize: '10px',
                      background: activity.details?.course_id === 'eu-geographie'
                        ? 'rgba(66, 165, 245, 0.2)'
                        : 'rgba(76, 175, 80, 0.2)',
                      padding: '2px 6px',
                      borderRadius: '4px',
                    }}>
                      {getCourseLabel(activity.details?.course_id || '')}
                    </span>
                  </div>
                  <div style={{
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: '12px',
                  }}>
                    {formatActivityDetails(activity.action, activity.details)}
                  </div>
                </div>
                <div style={{
                  textAlign: 'right',
                  color: 'rgba(255,255,255,0.4)',
                  fontSize: '11px',
                }}>
                  <div>{formatDate(activity.created_at)}</div>
                  <div>{formatTime(activity.created_at)}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ============================================ */}
      {/* TREND CHART */}
      {/* ============================================ */}
      <div style={{ marginTop: '24px' }}>
        <h3 style={{
          color: '#ffd54f',
          fontSize: '18px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          📈 Mein Trend
        </h3>
        <TrendChart quizResults={allQuizResults} />
      </div>

      {/* ============================================ */}
      {/* MEDALS */}
      {/* ============================================ */}
      <div style={{ marginTop: '24px' }}>
        <h3 style={{
          color: '#ffd54f',
          fontSize: '18px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          🏆 Meine Medaillen
          <span style={{
            fontSize: '13px',
            color: 'rgba(255,255,255,0.5)',
            fontWeight: 400,
          }}>
            ({achievedMedals}/{MEDALS.length})
          </span>
        </h3>

        {/* Course filter tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '16px',
          overflowX: 'auto',
        }}>
          <button
            onClick={() => setMedalCourseFilter(undefined)}
            style={{
              padding: '8px 14px',
              borderRadius: '10px',
              border: 'none',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              background: !medalCourseFilter ? '#ffd54f' : 'rgba(255,255,255,0.08)',
              color: !medalCourseFilter ? '#1a1a2e' : 'rgba(255,255,255,0.6)',
            }}
          >
            Alle
          </button>
          {visibleCourses.map(c => (
            <button
              key={c.id}
              onClick={() => setMedalCourseFilter(c.id)}
              style={{
                padding: '8px 14px',
                borderRadius: '10px',
                border: 'none',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                background: medalCourseFilter === c.id ? '#ffd54f' : 'rgba(255,255,255,0.08)',
                color: medalCourseFilter === c.id ? '#1a1a2e' : 'rgba(255,255,255,0.6)',
              }}
            >
              {c.icon} {c.title}
            </button>
          ))}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '10px',
        }}>
          {medalStatuses.map(status => (
            <MedalCard key={status.medal.id} status={status} />
          ))}
        </div>
      </div>

      {/* ============================================ */}
      {/* RECENT QUIZ RESULTS (original) */}
      {/* ============================================ */}
      <div className="profile-section" style={{ marginTop: '24px' }}>
        <h3>
          <Calendar size={18} />
          Letzte Quiz-Ergebnisse
        </h3>
        {recentResults.length === 0 ? (
          <p className="empty-text">Noch keine Quiz-Ergebnisse</p>
        ) : (
          <div className="results-list">
            {recentResults.map((result) => (
              <div key={result.id} className="result-row">
                <span className="result-date">{formatDate(result.created_at)}</span>
                <span className="result-course">{getCourseLabel(result.course_id)}</span>
                <span className={`result-score ${result.percentage >= 70 ? 'good' : 'needs-work'}`}>
                  {result.percentage}%
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* BUTTONS */}
      {/* ============================================ */}
      <Link to="/feedback" className="btn-secondary" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '14px 20px',
        borderRadius: '12px',
        background: 'rgba(171, 71, 188, 0.15)',
        border: '1px solid rgba(171, 71, 188, 0.3)',
        color: '#ce93d8',
        fontSize: '15px',
        fontWeight: 600,
        textDecoration: 'none',
        marginBottom: '12px',
        marginTop: '20px',
      }}>
        KI-Feedback Historie
      </Link>

      {user.role === 'admin' && (
        <Link to="/admin" className="btn-admin">
          <Shield size={20} />
          Admin Dashboard
        </Link>
      )}

      <button
        onClick={() => setShowResetModal(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          width: '100%',
          padding: '14px 20px',
          background: 'rgba(255, 152, 0, 0.1)',
          border: '1px solid rgba(255, 152, 0, 0.3)',
          borderRadius: '12px',
          color: '#ff9800',
          fontSize: '15px',
          fontWeight: 600,
          cursor: 'pointer',
          marginBottom: '12px',
        }}
      >
        <RotateCcw size={20} />
        Fortschritt zuruecksetzen
      </button>

      <button className="btn-logout" onClick={logout}>
        <LogOut size={20} />
        Abmelden
      </button>

      {/* Reset Modal with Level 3 (all progress) option */}
      <ResetModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        userId={user.id}
        showLevel3={true}
        onResetComplete={() => {
          // Reload data after reset
          setStreak(0);
          setRecentResults([]);
          setAllQuizResults([]);
          setActivityLog([]);
          setDailyStreaks([]);
          setFlashcardProgress([]);
        }}
      />
    </div>
  );
}
